// Phase 8 D2 — URL-search-param state for the dashboard filter bar.
//
// The route at `/_app/` declares `validateSearch: dashboardFiltersSchema`, so
// TanStack Router applies the schema's defaults on every navigation and
// strips unknown keys. This hook is a thin reader/writer over those typed
// search params:
//   - `filters` is the typed `DashboardFilters` value parsed from the URL.
//   - `setFilters(patch)` merges the patch into the current search params and
//     navigates to the same route, which causes every `useQuery(filters)`
//     subscriber to re-render with the new input.
//   - `reset()` navigates to the route with an empty search object — the
//     schema then re-fills from defaults (YTD, period_end, all providers,
//     active customers).
//
// `from: "/_app/"` is the route id from the generated route tree;
// `useSearch({ from })` resolves to the route's typed search-params shape.
// `useNavigate({ from: "/_app/" })` is anchored to the same id so the
// `search:` updater is typed against `DashboardFilters`. The `to:` field is
// the user-facing path, which is `/` because the dashboard route's URL is
// the app root under the `_app` layout.

import type { DashboardFilters } from "@payrollmart-invoice/shared";
import { useNavigate, useSearch } from "@tanstack/react-router";

export type UseDashboardFiltersReturn = {
	filters: DashboardFilters;
	setFilters: (patch: Partial<DashboardFilters>) => void;
	reset: () => void;
};

export function useDashboardFilters(): UseDashboardFiltersReturn {
	const filters = useSearch({ from: "/_app/" });
	const navigate = useNavigate({ from: "/" });

	const setFilters = (patch: Partial<DashboardFilters>) => {
		navigate({
			search: (prev: DashboardFilters) => ({ ...prev, ...patch }) as DashboardFilters,
		});
	};

	const reset = () => {
		// Empty search → `validateSearch` re-fills from schema defaults, URL
		// becomes plain `/`.
		navigate({ search: {} as DashboardFilters });
	};

	return { filters, setFilters, reset };
}
